import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
} from '@angular/core';

import { Category } from 'src/app/shared/model/reprovation';
import {
  IPill,
  IPillOutput,
} from 'src/app/components/pills/entities/pills.interface';

@Component({
  selector: 'app-category-select',
  templateUrl: './category-select.component.html',
  styleUrls: ['./category-select.component.scss'],
})
export class CategorySelectComponent implements OnChanges {
  @Input() labelSelect: string;
  @Input() labelSelected: string;
  @Input() noMarginTop: boolean;

  @Output() onChange = new EventEmitter<Category[]>();

  pills: IPill[];
  selectedCategory: Category;
  selectedSubCategories: Category[] = [];

  private _categories: Category[];
  private pendingExpandedCategoryId: number | null = null;

  @Input()
  get categories(): Category[] {
    return this._categories;
  }
  set categories(categories: Category[]) {
    this._categories = categories;
    this.pills =
      categories?.map(({ id, name }: Category) => ({ id, name })) || [];

    this.setSelectedCategory();
  }

  @Input() set subCategoriesSelected(subCategories: Category[]) {
    if (
      !!subCategories?.length &&
      !this.selectedCategory &&
      !this.selectedSubCategories.length
    ) {
      this.selectedSubCategories = subCategories;
      this.setSelectedCategory();
    }
  }

  /**
   * Opens a category with nothing marked. Stored rather than resolved, because
   * the lookup needs `categories`: resolving in the setter made this depend on
   * the order the consumer's template happens to bind the two, and the reverse
   * order expanded nothing while the caller still reported the offer as
   * applied. `ngOnChanges` runs after Angular has written every input, so the
   * question disappears instead of being documented.
   */
  @Input() set expandedCategoryId(categoryId: number | null | undefined) {
    // `== null` and not truthiness: these ids come from the catalogue, and a
    // category with id 0 is a category.
    this.pendingExpandedCategoryId = categoryId ?? null;
  }

  /**
   * Resolves the pending expansion once every input has landed. Kept pending
   * rather than consumed on a miss, so a `categories` list that arrives in a
   * later change detection cycle still honours it -- the old setter ran once
   * and gave up.
   *
   * Chips win: `selectedCategory` is already set by `subCategoriesSelected` (or
   * by the operator) by the time this runs, and expanding then would lose that
   * selection, which is what the guard has always been for.
   */
  ngOnChanges(): void {
    if (this.pendingExpandedCategoryId == null || this.selectedCategory) return;

    const index =
      this.categories?.findIndex(
        ({ id }: Category) => id === this.pendingExpandedCategoryId
      ) ?? -1;

    if (index < 0) return;

    // Consumed: kept pending it would re-expand on the next input change if
    // anything ever cleared `selectedCategory` -- `pillsChanged` sets it from a
    // lookup that can return undefined -- and re-opening a category the
    // operator had just closed is not this input's business.
    this.pendingExpandedCategoryId = null;
    this.pills[index].selected = true;
    this.selectedCategory = this.categories[index];
  }

  pillsChanged(pill: IPillOutput): void {
    this.selectedCategory = this.categories.find(
      ({ id }: Category) => id === pill.id
    );
  }

  selectSubCategory(subCategory: Category): void {
    document.body.click();

    const index = this.selectedSubCategoriesIds.indexOf(subCategory.id);

    if (index > -1) {
      this.selectedSubCategories.splice(index, 1);
    } else {
      this.selectedSubCategories.push(subCategory);
    }

    this.onChange.emit(this.selectedSubCategories);
  }

  subCategorySelected(subCategoryId: number): boolean {
    return this.selectedSubCategoriesIds.includes(subCategoryId);
  }

  private get selectedSubCategoriesIds(): number[] {
    return this.selectedSubCategories.map(({ id }) => id);
  }

  private setSelectedCategory(): void {
    if (this.categories?.length && this.selectedSubCategories?.length) {
      this.categories.some((category: Category, i: number) => {
        const match = category.children.find(({ id }: Category) =>
          this.selectedSubCategoriesIds.includes(id)
        );

        if (!!match) {
          this.pills[i].selected = true;
          this.selectedCategory = category;
        }

        return match;
      });
    }
  }
}
